import { Money } from './Money';
import { Badge } from './Badge';
import { Pagination, usePagination } from './Pagination';
import { formatDate } from '../lib/format';
import { CASH_METHOD_LABEL, type BusinessLedger, type CashMethod } from '../api/billing';

const methodLabel = (m: string | null) => (m ? CASH_METHOD_LABEL[m as CashMethod] ?? m : '—');

/**
 * Customer account ledger — account summary + chronological invoices (debit) /
 * payments (credit) with a running balance. The Laravel "contacts ledger" (img #6)
 * equivalent. Shared by reseller CustomerDetail + admin BusinessDetail.
 */
export function LedgerView({ ledger }: { ledger?: BusinessLedger }) {
  if (!ledger) {
    return <div className="py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">Loading…</div>;
  }
  const { summary, rows } = ledger;
  // The ledger is the one list here with no filters of its own, so pagination is
  // the only thing bounding it — a long-lived business accumulates a row per
  // invoice AND per payment (#12).
  const pager = usePagination(rows);
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile label="Total invoiced" minor={summary.totalInvoicedMinor} />
        <Tile label="Total paid" minor={summary.totalPaidMinor} accent="emerald" />
        <Tile label="Balance due" minor={summary.balanceDueMinor} accent={summary.balanceDueMinor > 0 ? 'rose' : undefined} />
        <Tile label="Opening balance" minor={summary.openingBalanceMinor} />
      </div>

      <div className="overflow-x-auto nice-scroll -mx-6">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
            <tr>
              <th className="text-left font-medium px-6 py-3">Date</th>
              <th className="text-left font-medium px-4 py-3">Reference</th>
              <th className="text-left font-medium px-4 py-3">Type</th>
              <th className="text-right font-medium px-4 py-3">Debit</th>
              <th className="text-right font-medium px-4 py-3">Credit</th>
              <th className="text-right font-medium px-4 py-3">Balance</th>
              <th className="text-left font-medium px-6 py-3">Method</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">No invoices or payments yet.</td></tr>
            )}
            {pager.pageItems.map((r, i) => (
              <tr key={`${r.kind}-${r.ref}-${i}`} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                <td className="px-6 py-3 whitespace-nowrap tabular-nums text-zinc-700 dark:text-zinc-200">{r.date ? formatDate(r.date, { compact: true }) : '—'}</td>
                <td className="px-4 py-3 mono text-xs text-zinc-600 dark:text-zinc-300">{r.ref}</td>
                <td className="px-4 py-3">
                  <Badge tone={r.kind === 'payment' ? 'success' : 'neutral'}>{r.kind === 'payment' ? 'Payment' : 'Invoice'}</Badge>
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {r.debitMinor ? <Money minor={r.debitMinor} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" /> : <span className="text-zinc-300 dark:text-zinc-600">—</span>}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {r.creditMinor ? <Money minor={r.creditMinor} hideCents tone="neutral" className="text-emerald-700 dark:text-emerald-300" /> : <span className="text-zinc-300 dark:text-zinc-600">—</span>}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  <Money minor={r.balanceMinor} hideCents tone="neutral" className="text-zinc-700 dark:text-zinc-200 font-medium" />
                </td>
                <td className="px-6 py-3 text-zinc-600 dark:text-zinc-300">{r.kind === 'payment' ? methodLabel(r.method) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="-mx-6">
        <Pagination state={pager} label="entries" />
      </div>
    </div>
  );
}

function Tile({ label, minor, accent }: { label: string; minor: number; accent?: 'emerald' | 'rose' }) {
  const tone = accent === 'emerald'
    ? 'text-emerald-700 dark:text-emerald-300'
    : accent === 'rose'
      ? 'text-rose-600 dark:text-rose-400'
      : 'text-zinc-950 dark:text-zinc-50';
  return (
    <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4">
      <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">{label}</div>
      <div className="mt-1.5 text-lg font-semibold">
        <Money minor={minor} hideCents tone="neutral" className={tone} />
      </div>
    </div>
  );
}
